import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Tabs, Tab, Box } from '@mui/material';
import { tabsStyles } from './styles';
import { Images, SaveParams } from 'utils';
import { SimpleTooltip } from '../tooltip';

function TabPanel(props) {
    const { boxClassName, children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`scrollable-force-tabpanel-${index}`}
            aria-labelledby={`scrollable-force-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box className={boxClassName} p={3}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `scrollable-force-tab-${index}`,
        'aria-controls': `scrollable-force-tabpanel-${index}`,
    };
}

export const SimpleTabs = ({ tabsLabels, tabsContent, defaultTab, getTab, tab, small, customTabPanel }) => {
    const tabStyle = tabsStyles();
    const [value, setValue] = React.useState(defaultTab);
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const allowedTabs = tabsLabels?.filter((t) => t)?.map((t) => t?.label) || [];
    const selectedTab = tab ? tab : value;
    const fallbackTab = allowedTabs.includes(defaultTab) ? defaultTab : allowedTabs[0];
    const activeTab = !allowedTabs.length || allowedTabs.includes(selectedTab) ? selectedTab : fallbackTab;

    useEffect(() => {
        if (info?.activeTab) {
            setValue(info?.activeTab);
        }
    }, [info]);

    const handleChange = (newValue) => {
        setValue(newValue);
        const anotherInfo = {
            ...info,
            activeTab: newValue,
        };
        delete anotherInfo.sortType;
        delete anotherInfo.orderType;
        delete anotherInfo.type;
        delete anotherInfo.status;
        delete anotherInfo.noteType;
        delete anotherInfo.tags;
        delete anotherInfo.tabType;
        if (getTab) {
            getTab(newValue);
        } else {
            SaveParams(navigate, { ...anotherInfo });
        }
    };

    const renderTabInfo = (tab) => {
        switch (tab) {
            case 'Notes':
                return {
                    icon: (
                        <SimpleTooltip title="Notes" placement="top" arrow>
                            <img src={Images.noteNewStyle} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'History':
                return {
                    icon: (
                        <SimpleTooltip title="History" placement="top" arrow>
                            <img src={Images.historyTab} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Comments':
                return {
                    icon: (
                        <SimpleTooltip title="Comments" placement="top" arrow>
                            <img src={Images.commentCircle} alt="Comments icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Files':
                return {
                    icon: (
                        <SimpleTooltip title="Files" placement="top" arrow>
                            <img src={Images.fileTab} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Errors':
                return {
                    icon: (
                        <SimpleTooltip title="Errors" placement="top" arrow>
                            <img src={Images.alertError} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Goal Progress':
                return {
                    icon: (
                        <SimpleTooltip title="Goal Progress" placement="top" arrow>
                            <img src={Images.insightsChart} alt="Goal Progress icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Videos':
                return {
                    icon: (
                        <SimpleTooltip title="Videos" placement="top" arrow>
                            <img src={Images.videos} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };
            case 'Sections':
                return {
                    icon: (
                        <SimpleTooltip title="Sections" placement="top" arrow>
                            <img src={Images.sections} alt="Notes icon" width={20} height={20} />
                        </SimpleTooltip>
                    ),
                };

            default:
                return {
                    text: tab,
                };
        }
    };

    return (
        <div className={tabStyle.root}>
            <AppBar className={tabStyle.tabHeader} position="static">
                <Tabs
                    value={activeTab}
                    // style={headerWidth ? { width: screenWidth > 1919 ? '100%' : headerWidth } : {}}
                    indicatorColor="primary"
                    className={tabStyle.collor}
                    aria-label="scrollable force tabs example"
                    scrollButtons
                    allowScrollButtonsMobile
                    variant="scrollable"
                >
                    {tabsLabels &&
                        tabsLabels
                            ?.filter((t) => t)
                            .map((tabLabel, index) => {
                                if (tabLabel) {
                                    // An explicit `text` opts out of the label → icon map, for
                                    // pages whose tab shares a name with an icon tab ('Notes').
                                    const tabInfo = tabLabel?.text ? { text: tabLabel.text } : renderTabInfo(tabLabel?.label);
                                    return (
                                        <Tab
                                            key={index}
                                            onClick={() => handleChange(tabLabel?.label)}
                                            className={tabStyle.tabLabel}
                                            label={tabInfo?.text || ''}
                                            value={tabLabel?.label}
                                            icon={tabInfo?.icon || null}
                                            iconPosition="start"
                                            {...a11yProps(tabLabel?.label)}
                                        />
                                    );
                                }
                            })}
                </Tabs>
            </AppBar>

            {tabsContent &&
                tabsContent.map((tabContent, index) => {
                    if (tabContent && tabContent?.tab === activeTab) {
                        return (
                            <div key={index}>
                                <TabPanel
                                    index={tabContent?.tab}
                                    boxClassName={customTabPanel ? customTabPanel : small ? tabStyle?.smallBoxStyle : tabStyle?.boxStyle}
                                    value={tabContent?.tab}
                                >
                                    {tabContent?.tabComponent}
                                </TabPanel>
                            </div>
                        );
                    }
                })}
        </div>
    );
};
